import { useCallback, useEffect } from 'react';
import { useIsIntegrationClient } from '@frameio/shared/contexts/ClientNameContext';
import type { OpenInBrowserMutationVariables } from '@frameio/shared/integrations/gql';
import { useOpenInBrowserMutation } from '@frameio/shared/integrations/gql';
import { useDesktopAppPlatform } from '@frameio/shared/resources/mounted-storage/hooks/useDesktopAppPlatform';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import type { PressEvent } from '@frameio/vapor';
import {
  AlertDialog,
  AlertDialogPrimaryAction,
  AlertDialogSecondaryAction,
  Box,
  InlineButton,
  Text,
} from '@frameio/vapor';
import { getDriveDownloadOption } from '../../shared/utils';
import TEXT from './generated/TEXT/TransferModal';

TEXT.DEFINE(
  {
    titleTransfer: 'Launching Frame.io Transfer...',
    titleDrive: 'Launching Frame.io Drive...',
    details: (onRetry: (e: PressEvent) => void) =>
      `Not opening? ${(
        <InlineButton onPress={onRetry}>Try again</InlineButton>
      )} or install the desktop app.`,
    close: 'Close',
  },
  { InlineButton }
);
const TRACKING_SOURCE = 'download_modal';
function downloadApp(
  url: string,
  isIntegrationClient: boolean,
  openInBrowser: ({
    variables: {
      input: { url },
    },
  }: {
    variables: OpenInBrowserMutationVariables;
  }) => void
) {
  return isIntegrationClient
    ? openInBrowser({ variables: { input: { url } } })
    : window.open(url, '_blank');
}

type AppLaunchModalProps = {
  variant: 'transfer' | 'drive';
  isShowing: boolean;
  onExit: () => void;
  onRetry: () => void;
};

/**
 * The AppLaunchModal allows users to download the Transfer or Drive app,
 * or retry a download in the app.
 */
export const AppLaunchModal = ({
  variant,
  isShowing,
  onExit,
  onRetry,
}: AppLaunchModalProps) => {
  const track = useTrackUnsafe();
  const isIntegrationClient = useIsIntegrationClient();
  const [openInBrowser] = useOpenInBrowserMutation({
    context: { resolveWithUXP: true },
  });
  const detectedPlatform = useDesktopAppPlatform();

  useEffect(() => {
    if (!isShowing || variant !== 'drive') return;
    track('drive-app-download-dialog-shown', { source: TRACKING_SOURCE });
  }, [isShowing, track, variant]);

  const handleDismiss = useCallback(() => {
    if (variant === 'drive') {
      track('drive-app-download-dialog-dismissed', { source: TRACKING_SOURCE });
    }
    onExit();
  }, [onExit, track, variant]);

  const downloadOption = getDriveDownloadOption(detectedPlatform, variant);

  return (
    <AlertDialog
      actions={
        <>
          <AlertDialogPrimaryAction
            onClick={() => {
              downloadApp(
                downloadOption.link,
                isIntegrationClient,
                openInBrowser
              );

              if (variant === 'drive') {
                track('drive-app-download-clicked', {
                  platform: detectedPlatform,
                  source: TRACKING_SOURCE,
                });
              }
            }}
          >
            {downloadOption.label}
          </AlertDialogPrimaryAction>

          <AlertDialogSecondaryAction
            onClick={handleDismiss}
            variant="tertiary"
          >
            {TEXT.close}
          </AlertDialogSecondaryAction>
        </>
      }
      details={
        <Box paddingBottom={8}>
          <Text textAlign="center" variant="body">
            {TEXT.details(onRetry)}
          </Text>
        </Box>
      }
      isShowing={isShowing}
      stackActionButtons={true}
      title={variant === 'drive' ? TEXT.titleDrive : TEXT.titleTransfer}
    />
  );
};
